import React from 'react';
import { Receipt, ShieldCheck, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export const InvoiceSection = ({
  workOrder, navigate,
  invoiceNumber, setInvoiceNumber, invoiceAmount, setInvoiceAmount,
  approvedBy, setApprovedBy, approvers,
  nextServiceDate, setNextServiceDate,
  totalPartsCost, totalLabourHT, totalHT, totalTTC, tvaRate,
  onComplete, completing, onGenerateInvoice,
}) => {
  return (
    <Card className="p-4 rounded-xl space-y-4" data-testid="invoice-section">
      <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Receipt className="w-4 h-4" /> Facturation & Cloture</h3>
      {workOrder.status === 'completed' ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-emerald-700">
              <ShieldCheck className="w-5 h-5" />
              <span className="font-semibold">Fiche cloturee</span>
            </div>
            {workOrder.invoice_number && (
              <span className="font-mono font-bold text-lg text-blue-700" data-testid="invoice-number-display">{workOrder.invoice_number}</span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Montant TTC</p>
              <p className="font-bold text-2xl text-slate-900" data-testid="invoice-amount-display">{workOrder.invoice_amount ? `${Number(workOrder.invoice_amount).toFixed(2)} CHF` : '—'}</p>
            </div>
            <div>
              <p className="text-slate-500">Approuve par</p>
              <p className="font-medium text-slate-900" data-testid="approved-by-display">{workOrder.approved_by_name}</p>
            </div>
          </div>
          {workOrder.completed_at && (
            <p className="text-xs text-slate-400">Le {workOrder.completed_at.substring(0, 10)} a {workOrder.completed_at.substring(11, 16)}</p>
          )}
          {workOrder.next_service_date && (
            <div className="flex items-center gap-2 mt-2 text-sm text-blue-600">
              <CalendarClock className="w-4 h-4" />
              <span>Prochain service : <strong>{workOrder.next_service_date}</strong></span>
            </div>
          )}
          {!workOrder.invoice_id && workOrder.billing_type !== 'interne' && (
            <Button className="mt-3 bg-blue-600 hover:bg-blue-700" onClick={onGenerateInvoice} data-testid="generate-invoice-btn">
              <Receipt className="mr-2 w-4 h-4" /> Générer la facture automatiquement
            </Button>
          )}
          {workOrder.invoice_id && (
            <Button variant="outline" className="mt-3" onClick={() => navigate('/admin/billing')} data-testid="view-invoice-btn">
              <Receipt className="mr-2 w-4 h-4" /> Voir la facture {workOrder.invoice_number}
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-slate-500">Renseignez le numéro de facture et l'approbateur pour clôturer la fiche de travail.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">N de facture *</Label>
              <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value.toUpperCase())} placeholder="GDV-2026-000001" data-testid="invoice-number-input" />
              <p className="text-xs text-slate-400">Format: GDV-YYYY-NNNNNN</p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Montant TTC (CHF)</Label>
              <Input type="number" step="0.01" min="0" value={invoiceAmount} onChange={(e) => setInvoiceAmount(e.target.value)} placeholder="0.00" data-testid="invoice-amount-input" />
              {totalHT > 0 && (
                <p className="text-xs text-slate-400">
                  Pieces: {totalPartsCost.toFixed(2)} + M.O: {totalLabourHT.toFixed(2)} = {totalHT.toFixed(2)} HT + TVA {tvaRate}% = <strong>{totalTTC.toFixed(2)} TTC</strong>
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Approuve par *</Label>
              <Select value={approvedBy} onValueChange={setApprovedBy}>
                <SelectTrigger data-testid="approved-by-select"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                <SelectContent>
                  {approvers.map(a => (
                    <SelectItem key={a.id} value={a.id} data-testid={`approver-${a.id}`}>
                      {a.first_name} {a.last_name} {a.is_admin ? '(Admin)' : '(Reception)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Prochain service</Label>
              <Input type="date" value={nextServiceDate} onChange={(e) => setNextServiceDate(e.target.value)} data-testid="next-service-date-input" />
              <p className="text-xs text-slate-400">Auto +12 mois</p>
            </div>
          </div>
          <Button onClick={onComplete} disabled={completing || (!invoiceNumber && workOrder.billing_type !== 'interne') || !approvedBy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="complete-workorder-btn">
            <ShieldCheck className="mr-2 w-4 h-4" />
            {completing ? 'Cloture en cours...' : 'Cloturer la fiche'}
          </Button>
        </div>
      )}
    </Card>
  );
};
